import { useEffect, useRef, useMemo, useCallback } from 'react';
import { MediaAttachmentCard } from './Admin/AttachmentGroups/MediaAttachmentCard';

interface ShowcaseAttachment {
  id: number;
  title?: string;
  public_url: string;
  description?: string;
}

interface AutoScrollColumnProps {
  scrollDirection: 'down' | 'up';
  children: React.ReactNode;
  scrollSpeed?: number; // pixels per second
}

const AutoScrollColumn: React.FC<AutoScrollColumnProps> = ({
  scrollDirection,
  children,
  scrollSpeed = 30 // pixels per second
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    const contentElement = contentRef.current;
    if (!scrollElement || !contentElement) return;

    // Clone content for seamless infinite scroll
    const clonedContent = contentElement.cloneNode(true) as HTMLDivElement;
    clonedContent.setAttribute('aria-hidden', 'true'); // For accessibility
    scrollElement.appendChild(clonedContent);

    // Set initial position
    if (scrollDirection === 'up') {
      scrollElement.scrollTop = contentElement.offsetHeight;
    } else {
      scrollElement.scrollTop = 0;
    }

    const animate = (currentTime: number) => {
      if (!isPausedRef.current) {
        if (lastTimeRef.current === 0) {
          lastTimeRef.current = currentTime;
        }

        const deltaTime = (currentTime - lastTimeRef.current) / 1000; // Convert to seconds
        lastTimeRef.current = currentTime;

        if (scrollElement && contentElement) {
          const scrollIncrement = scrollSpeed * deltaTime;
          const contentHeight = contentElement.offsetHeight;

          if (scrollDirection === 'down') {
            scrollElement.scrollTop += scrollIncrement;

            // Reset to start when we've scrolled one full content height
            if (scrollElement.scrollTop >= contentHeight) {
              scrollElement.scrollTop -= contentHeight;
            }
          } else {
            scrollElement.scrollTop -= scrollIncrement;

            // Reset to end when we've scrolled to the top
            if (scrollElement.scrollTop <= 0) {
              scrollElement.scrollTop += contentHeight;
            }
          }
        }
      } else {
        // Keep updating lastTimeRef even when paused to avoid jumps
        lastTimeRef.current = currentTime;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      // Remove cloned content
      if (scrollElement && clonedContent.parentNode === scrollElement) {
        scrollElement.removeChild(clonedContent);
      }
    };
  }, [scrollDirection, scrollSpeed, children]);

  // Pause on hover for better UX
  const handleMouseEnter = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    isPausedRef.current = false;
    lastTimeRef.current = 0; // Reset to avoid time jump
  }, []);

  return (
    <div
      ref={scrollRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        flex: 1,
        overflowY: 'hidden', // Hide scrollbar
        position: 'relative',
        cursor: 'pointer', // Indicate interactivity
      }}
    >
      <div ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

interface ShowcaseGalleryProps {
  attachments?: ShowcaseAttachment[];
  scrollSpeed?: number;
  baseHeight?: number; // Base height for calculating variations
}

const ShowcaseGallery: React.FC<ShowcaseGalleryProps> = ({
  attachments = [],
  scrollSpeed = 60,
  baseHeight = 150 // Base height in pixels
}) => {
  // Define height multipliers for visual variety
  // These will be multiplied by baseHeight
  const heightMultipliers = [0.8, 2.0, 1.5, 1.8, 1.2, 2.2, 1.3, 1.6];

  // Convert multipliers to actual pixel heights
  const heightsInPixels = useMemo(() =>
    heightMultipliers.map(multiplier => Math.round(baseHeight * multiplier)),
    [baseHeight]
  );

  // Generate placeholder attachments if none provided
  const items = useMemo(() => {
    if (attachments.length > 0) {
      return attachments;
    }
    // Generate 16 placeholder attachments for demo
    return Array.from({ length: 16 }, (_, i) => ({
      id: i,
      title: `Image ${i + 1}`,
      public_url: `https://picsum.photos/seed/${i}/800/450`,
    }));
  }, [attachments]);

  // Split attachments into two columns
  const midpoint = Math.ceil(items.length / 2);
  const column1Items = items.slice(0, midpoint);
  const column2Items = items.slice(midpoint);

  // Ensure we have enough content for smooth scrolling
  const minItems = 4;
  const finalColumn1 = column1Items.length < minItems
    ? [...column1Items, ...column1Items, ...column1Items]
    : column1Items;
  const finalColumn2 = column2Items.length < minItems
    ? [...column2Items, ...column2Items, ...column2Items]
    : column2Items;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        gap: '5px',
      }}
    >
      <AutoScrollColumn scrollDirection="down" scrollSpeed={scrollSpeed}>
        {finalColumn1.map((attachment, index) => (
          <div key={`col1-${index}`} style={{ marginBottom: '5px' }}>
            <MediaAttachmentCard
              attachment={attachment}
              height={heightsInPixels[index % heightsInPixels.length]}
            />
          </div>
        ))}
      </AutoScrollColumn>
      <AutoScrollColumn scrollDirection="up" scrollSpeed={scrollSpeed}>
        {finalColumn2.map((attachment, index) => (
          <div key={`col2-${index}`} style={{ marginBottom: '5px' }}>
            <MediaAttachmentCard
              attachment={attachment}
              height={heightsInPixels[(index + 2) % heightsInPixels.length]} // Offset for variety
            />
          </div>
        ))}
      </AutoScrollColumn>
    </div>
  );
};

export default ShowcaseGallery;